import { createSelector } from '@reduxjs/toolkit';
import { isEqual, startCase } from 'lodash';

import {
  VerificationChecksAnalyticsQueryParams,
  VerificationVolumeAnalyticsQueryParams,
} from 'dashboard/models/analytics-query-template';
import {
  FilterOptionKey,
  VerificationTemplatesFilterOptions,
} from 'dashboard/models/metrics-verification';
import { getAdjustedFiltersForSelectors } from 'dashboard/redux/selectors/metrics';
import {
  DynamicFilterOptions,
  VerificationMetricsFilterParams,
} from 'dashboard/redux/slices/metrics-verification';
import { RootState } from 'dashboard/redux/store';
import { FilterDimensions } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import { GOVERNMENT_ID_DISPLAY_STRINGS, GovernmentIdClass } from 'lib/constants/governmentIds';
import { ApiModel } from 'lib/interfaces/api';

// TODO(amateo): https://withpersona.atlassian.net/browse/PER-19950
const getVerificationFilters = (state: RootState) => state.metricsVerification.filters;
const getVerificationTemplates = (state: RootState) =>
  state.metricsVerification.filterOptions.verificationTemplates;

const adjustFilterOptions = (options: string[]) =>
  [...options].sort().map((option) => ({ label: startCase(option), value: option }));

const governmentIdsToOptions = (options: string[]) =>
  // dz: unfortunately we have some bad data that snuck into persona-web/snowflake.
  // we occasionally see nv as a value, which should not be valid.
  // however when we see a value that we don't recognize it is probably better to
  // show the raw value (since the data is in there) instead of showing a blank,
  // thus the fallback
  [...options].sort().map((option) => ({
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    label: GOVERNMENT_ID_DISPLAY_STRINGS[option as GovernmentIdClass] ?? option,
    value: option,
  }));

const getFilterOptionsByKey = (state: RootState, key: keyof DynamicFilterOptions) =>
  state.metricsVerification.filterOptions[key];

const createAdjustedFilterOptionSelector = (key: keyof DynamicFilterOptions) =>
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  createSelector(
    (state: RootState) => getFilterOptionsByKey(state, key),
    key === FilterOptionKey.GovernmentIdTypes ? governmentIdsToOptions : adjustFilterOptions,
  );

export const getDynamicFilters = createSelector(
  // NOTE: Ordering is really important here, double check that any added filters are in the proper positions! ty
  [
    createAdjustedFilterOptionSelector(FilterOptionKey.AamvaIssuingAuthorities),
    createAdjustedFilterOptionSelector(FilterOptionKey.AamvaDeviceGroups),
    createAdjustedFilterOptionSelector(FilterOptionKey.AamvaPlatforms),
    createAdjustedFilterOptionSelector(FilterOptionKey.DatabaseCountryCodes),
    createAdjustedFilterOptionSelector(FilterOptionKey.DatabaseDeviceGroups),
    createAdjustedFilterOptionSelector(FilterOptionKey.DatabasePlatforms),
    createAdjustedFilterOptionSelector(FilterOptionKey.GovernmentIdCaptureMethods),
    createAdjustedFilterOptionSelector(FilterOptionKey.GovernmentIdDeviceGroups),
    createAdjustedFilterOptionSelector(FilterOptionKey.GovernmentIdIssuingCountries),
    createAdjustedFilterOptionSelector(FilterOptionKey.GovernmentIdIssuingSubdivisions),
    createAdjustedFilterOptionSelector(FilterOptionKey.GovernmentIdSelectedCountries),
    createAdjustedFilterOptionSelector(FilterOptionKey.GovernmentIdTypes),
    createAdjustedFilterOptionSelector(FilterOptionKey.SelfieCaptureMethods),
    createAdjustedFilterOptionSelector(FilterOptionKey.SelfieDeviceGroups),
    createAdjustedFilterOptionSelector(FilterOptionKey.SelfiePlatforms),
  ],
  (
    aamvaAuthorities,
    aamvaDeviceGroups,
    aamvaPlatforms,
    databaseCountryCodes,
    databaseDeviceGroups,
    databasePlatforms,
    govIDCaptureMethods,
    govIDDeviceGroups,
    govIDIssuingCountries,
    govIDSubdivisions,
    govIDSelectedCountries,
    govIDTypes,
    selfieCaptureMethods,
    selfieDeviceGroups,
    selfiePlatforms,
  ) => ({
    [ApiModel.VerificationAamva]: {
      [FilterDimensions.IssuingAuthority]: aamvaAuthorities,
      [FilterDimensions.DeviceGroup]: aamvaDeviceGroups,
      [FilterDimensions.PlatformIntegration]: aamvaPlatforms,
    },
    [ApiModel.VerificationGovernmentId]: {
      [FilterDimensions.IssuingSubdivision]: govIDSubdivisions,
      // The additional filter dimensions are feature-flagged for the initial rollout.
      // If a FilterDimension is not present, default hardcoded values will be used
      // from VERIFICATION_VALUES_OPTIONS.
      ...{
        [FilterDimensions.CaptureMethod]: govIDCaptureMethods,
        [FilterDimensions.CountrySelect]: govIDSelectedCountries,
        [FilterDimensions.DeviceGroup]: govIDDeviceGroups,
        [FilterDimensions.IdClass]: govIDTypes,
        [FilterDimensions.IssuingCountry]: govIDIssuingCountries,
      },
    },
    [ApiModel.VerificationSelfie]: {
      // The additional filter dimensions are feature-flagged for the initial rollout.
      // If a FilterDimension is not present, default hardcoded values will be used
      // from VERIFICATION_VALUES_OPTIONS.
      ...{
        [FilterDimensions.CaptureMethod]: selfieCaptureMethods,
        [FilterDimensions.DeviceGroup]: selfieDeviceGroups,
        [FilterDimensions.PlatformIntegration]: selfiePlatforms,
      },
    },
    [ApiModel.VerificationDatabase]: {
      // The additional filter dimensions are feature-flagged for the initial rollout.
      // If a FilterDimension is not present, default hardcoded values will be used
      // from VERIFICATION_VALUES_OPTIONS.
      ...{
        [FilterDimensions.CountryCode]: databaseCountryCodes,
        [FilterDimensions.DeviceGroup]: databaseDeviceGroups,
        [FilterDimensions.PlatformIntegration]: databasePlatforms,
      },
    },
  }),
);

export const getAdjustedFilters = getAdjustedFiltersForSelectors<VerificationMetricsFilterParams>([
  getVerificationFilters,
]);

export const getVerificationTemplatesByType = createSelector(
  [getVerificationTemplates],
  (templates: VerificationTemplatesFilterOptions) =>
    Object.fromEntries(
      Object.entries(templates).map(([key, value]) => [
        key,
        value.map(({ name, token }) => ({ name, id: token })),
      ]),
    ),
);

export const getSelectedVolumeQueryTemplate = (state: RootState) =>
  state.metricsVerification.selectedVolumeQueryTemplate;

export const getIsSelectedVolumeQueryTemplateDifferentFromFilters = createSelector(
  [getSelectedVolumeQueryTemplate, getVerificationFilters],
  (selectedQueryTemplate, filters) => {
    if (selectedQueryTemplate.template == null) {
      return false;
    }

    const {
      attributes: { query },
    } = selectedQueryTemplate.template;
    const queryParams = query as VerificationVolumeAnalyticsQueryParams;
    return (
      !isEqual(
        [...queryParams['template-ids'], ...queryParams['inquiry-template-ids']],
        filters.templateIds,
      ) ||
      queryParams['verification-types'] !== filters.verificationTypes ||
      queryParams['group-by'] !== filters.groupBy ||
      selectedQueryTemplate.computedOffsetStart !== filters.createdAtStart ||
      selectedQueryTemplate.computedOffsetEnd !== filters.createdAtEnd
    );
  },
);

export const getSelectedChecksQueryTemplate = (state: RootState) =>
  state.metricsVerification.selectedChecksQueryTemplate;

export const getIsSelectedChecksQueryTemplateDifferentFromFilters = createSelector(
  [getSelectedChecksQueryTemplate, getVerificationFilters],
  (selectedQueryTemplate, filters) => {
    if (selectedQueryTemplate.template == null) {
      return false;
    }

    const {
      attributes: { query },
    } = selectedQueryTemplate.template;
    const queryParams = query as VerificationChecksAnalyticsQueryParams;
    return (
      !isEqual(
        [...queryParams['template-ids'], ...queryParams['inquiry-template-ids']],
        filters.templateIds,
      ) ||
      queryParams['verification-types'] !== filters.verificationTypes ||
      queryParams['group-by'] !== filters.groupBy ||
      queryParams['check-types'] !== filters.checkTypes ||
      queryParams['check-requirements'] !== filters.checkRequirements ||
      selectedQueryTemplate.computedOffsetStart !== filters.createdAtStart ||
      selectedQueryTemplate.computedOffsetEnd !== filters.createdAtEnd
    );
  },
);
